import React, { useState, useReducer, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Checkbox, Form, Radio } from 'semantic-ui-react'
import * as R from 'ramda'
import CustomUserAccessBox from 'components/SurveyAccessFormModal/CustomUserAccessBox'
import { TooltipLabel } from 'components/TooltipLabel'
import { reducer } from 'components/SurveyAccessFormModal/reducer'
import useAxios, { mutate } from 'hooks/useAxios'
import toast from 'libs/toast'
import useSaveUserActivityLog, { CUSTOM_DASHBOARD_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useWorkspace from 'hooks/useWorkspace'
import useUser from 'hooks/useUser'
import useFeatureToggle from 'hooks/useFeatureToggle'
import usePermission from 'hooks/usePermission'
import constants from 'config/constants'
import BenchmarkInfoContent, { BENCHMARK_HELP_URL } from 'components/BenchmarkInfoContent'

export default function CustomDashboardModal({
  modal: Modal,
  close,
  setAllowedUserAccess,
  setCustomDashboardName,
  setHideFeedbackSidebar,
  setShowBenchmark,
  users = [],
  dashboard = null,
  customDashboardName,
  hideFeedbackSideBar = false,
  showBenchmark = true,
  isUpdate = false,
  onDashboardUpdated,
}) {
  const history = useHistory()
  const saveUserActivityLog = useSaveUserActivityLog()
  const _isAccessEditableUser = (user) => user.is_access_editable
  const accessEditableUser = users.filter(_isAccessEditableUser)
  const initialState = { users: accessEditableUser }

  const [state, dispatch] = useReducer(reducer, initialState)
  const initialSelectedAll = R.all((user) => user.has_permission, users)
  const [isSelectedAll, setIsSelectedAll] = useState(initialSelectedAll)
  const [isHideFeedbackSideBar, setIsHideFeedbackSidebar] = useState(hideFeedbackSideBar)
  const [isShowBenchmark, setIsShowBenchmark] = useState(showBenchmark)
  const [userSearch, setUserSearch] = useState('')
  const dashboardNameRef = useRef(customDashboardName)
  const hiddenRefButton = useRef(null)
  const userInfo = useUser()
  const { enableBenchmarks } = useFeatureToggle()
  const { hasPermission } = usePermission()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)
  const workspaceToken = useWorkspace()

  const { fetch: createDashboard, loading: isCreatingDashboard } = useAxios({
    method: 'post',
    url: 'dashboard/dashboard/',
    skip: true,
  })

  const { fetch: updateDashboard, loading: isUpdatingDashboard } = useAxios({
    method: 'patch',
    url: `dashboard/dashboards/${R.prop('token', dashboard)}`,
    skip: true,
  })

  function clearAllOptions(event) {
    event.preventDefault()
    dispatch({ type: 'assign_permission_to_all_user', payload: false })
  }

  function selectAllOptions(event) {
    event.preventDefault()
    dispatch({ type: 'assign_permission_to_all_user', payload: true })
  }

  function onCheckboxChange(user) {
    if (user.has_permission) {
      dispatch({ type: 'remove_permission', payload: user.user_id })
    } else {
      dispatch({ type: 'add_permission', payload: user.user_id })
    }
  }

  function _getUserIds(users) {
    return users.map((user) => user.user_id)
  }

  function handleSearchChange(_, { value }) {
    setUserSearch(value)
  }

  function handleSubmit() {
    if (dashboard && dashboard.id) {
      handleUpdateDashboard()
    } else {
      handleCreateDashboard()
    }
  }

  function handleCreateDashboard() {
    const allowUserId = _getUserIds(state.users.filter((user) => user.has_permission))

    const data = {
      name: dashboardNameRef.current.value,
      allowed_user_access: allowUserId,
      hide_feedback_sidebar: isHideFeedbackSideBar,
      ...(canChooseBenchmark ? { show_benchmark: isShowBenchmark } : {}),
    }
    createDashboard({
      data: data,
    })
      .then((res) => {
        if (res.data) {
          const dashboardToken = res.data.token
          history.push({
            pathname: `/${workspaceToken}/dashboards/${dashboardToken}`,
            state: { useStickyFilter: false, dashboardName: res.data.name },
          })
          close()
          mutate('dashboard/dashboards/')
          saveUserActivityLog({
            action: CUSTOM_DASHBOARD_ACTION_NAME.created_dashboard,
            statusCode: res.status,
            requestBody: data,
            target: `com_${companyId}`,
          })
          toast.success('Nice work on creating a new dashboard! Now try adding a widget.')
        }
      })
      .catch((err) => {
        console.error(err)
        toast.error(
          'Uh oh. There was a problem creating the dashboard. Please try again or contact Simplesat support.'
        )
        saveUserActivityLog({
          action: CUSTOM_DASHBOARD_ACTION_NAME.created_dashboard,
          statusCode: err?.response?.status,
          errorResponseBody: err?.response?.data,
          requestBody: data,
          target: `com_${companyId}`,
        })
      })
  }

  function handleUpdateDashboard() {
    const allowUserId = _getUserIds(state.users.filter((user) => user.has_permission))
    const data = {
      name: customDashboardName,
      allowed_user_access: allowUserId,
      hide_feedback_sidebar: isHideFeedbackSideBar,
      ...(canChooseBenchmark ? { show_benchmark: isShowBenchmark } : {}),
    }

    updateDashboard({
      data: data,
    })
      .then((res) => {
        if (res.data) {
          setAllowedUserAccess(res.data.allowed_user_access)
          setHideFeedbackSidebar(res.data.hide_feedback_sidebar)
          setShowBenchmark?.(res.data.show_benchmark)
          setCustomDashboardName?.(res.data.name)
          onDashboardUpdated?.({
            hide_feedback_sidebar: res.data.hide_feedback_sidebar,
            show_benchmark: res.data.show_benchmark,
            name: res.data.name,
          })
          mutate('dashboard/dashboards/')
          saveUserActivityLog({
            action: CUSTOM_DASHBOARD_ACTION_NAME.updated_dashboard,
            statusCode: res.status,
            requestBody: data,
            target: `dashboard_${R.prop('token', dashboard)}`,
          })
          toast.success('Dashboard settings updated.')
          close()
        }
      })
      .catch((err) => {
        console.error(err)
        saveUserActivityLog({
          action: CUSTOM_DASHBOARD_ACTION_NAME.updated_dashboard,
          statusCode: err?.response?.status,
          errorResponseBody: err?.response?.data,
          requestBody: data,
          target: `dashboard_${R.prop('token', dashboard)}`,
        })
        toast.error(
          'Dashboard settings failed to update. Please try again or contact Simplesat support.'
        )
      })
  }

  function handleDashboardNameChange(e) {
    setCustomDashboardName?.(e.target.value)
  }

  const canChooseBenchmark = Boolean(enableBenchmarks)
  const hasUsers = state.users.length > 0 ? true : false
  const filteredUsers = state.users.filter((user) => {
    if (userSearch) {
      const foundSearchAtIndex = user.name.search(
        new RegExp(userSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
      )
      return foundSearchAtIndex > -1
    }
    return true
  })

  return (
    <React.Fragment>
      <Modal size="small">
        <Modal.Header>{isUpdate ? 'Update dashboard' : 'Create dashboard'}</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit}>
            <Form.Field>
              <label htmlFor="custom-dashboard-name">Name</label>
              <input
                id="custom-dashboard-name"
                required
                ref={dashboardNameRef}
                value={customDashboardName}
                onChange={handleDashboardNameChange}
                maxLength={300}
                placeholder="Custom dashboard name"
              />
            </Form.Field>
            <Form.Group grouped>
              <Form.Field>
                <Radio
                  label="All users in this account"
                  name="userAccessOption"
                  checked={isSelectedAll}
                  onChange={(event) => {
                    setIsSelectedAll(!isSelectedAll)
                    selectAllOptions(event)
                  }}
                />
              </Form.Field>
              <Form.Field>
                <Radio
                  label="All users with full survey access, plus the following users who require individual access:"
                  name="userAccessOption"
                  checked={!isSelectedAll}
                  onChange={() => setIsSelectedAll(!isSelectedAll)}
                />
              </Form.Field>
            </Form.Group>
            {isSelectedAll ? null : (
              <Form.Field>
                <CustomUserAccessBox
                  clearAllOptions={clearAllOptions}
                  currentUserId={null}
                  handleSearchChange={handleSearchChange}
                  onCheckboxChange={onCheckboxChange}
                  hasUsers={hasUsers}
                  users={filteredUsers}
                  selectAllOptions={selectAllOptions}
                />
              </Form.Field>
            )}
            <Form.Group grouped>
              <label>Display</label>
              <Form.Field>
                <Checkbox
                  label="Show feedback sidebar"
                  checked={!isHideFeedbackSideBar}
                  onChange={(event, { checked }) => setIsHideFeedbackSidebar(!checked)}
                />
              </Form.Field>
              {canChooseBenchmark && (
                <Form.Field>
                  <div className="flex items-center">
                    <Checkbox
                      label="Show benchmarks"
                      checked={isShowBenchmark}
                      onChange={(event, { checked }) => setIsShowBenchmark(checked)}
                    />
                    <TooltipLabel
                      descriptionTitle="Benchmarks"
                      descriptionContentWithFormat={
                        <BenchmarkInfoContent
                          workspaceToken={workspaceToken}
                          canManageWorkspaceSettings={hasPermission(
                            constants.permissions.manageAccountSettings
                          )}
                        />
                      }
                      learnMoreLink={BENCHMARK_HELP_URL}
                    />
                  </div>
                </Form.Field>
              )}
            </Form.Group>

            <input type="submit" hidden ref={hiddenRefButton} />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => hiddenRefButton.current.click()}
            disabled={isCreatingDashboard || isUpdatingDashboard}
            loading={isCreatingDashboard || isUpdatingDashboard}
            primary
          >
            {isUpdate ? 'Update dashboard' : 'Create dashboard'}
          </Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Dashboards/CustomDashboardModal/index.js